import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';

import WebPanelLocationQuery, {
	type WebPanelLocationQuery$data,
	type WebPanelLocationQuery$variables,
} from './__generated__/WebPanelLocationQuery.graphql.ts';

export function preloadWebPanelLocation({
	location = null,
	contentId = null,
	spaceKey = null,
}: {
	location?: string | string[] | null;
	contentId?: string | null;
	spaceKey?: string | null;
} = {}) {
	const variables = {
		// GraphQL doesn't cache single location strings properly when passed to locations
		// unless we're using multiple locations we should be using the location variable
		location: Array.isArray(location) ? null : location,
		locations: Array.isArray(location) ? location : [],
		contentId,
		spaceKey,
	};

	const result = query<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>({
		query: WebPanelLocationQuery,
		variables,
	});

	// When fetching multiple locations in a single batched request, the result is cached
	// under the combined array key { location: null, locations: [...] } by query() above.
	// However, consumers (e.g. <WebPanelLocation location={ATL_GENERAL} />) query with
	// individual location keys, causing a cache miss.
	// To fix this, we split the batched result and write each location's panels back into
	// the cache under its individual key so consumers get a cache hit.
	if (
		Array.isArray(location) &&
		location.length > 0 &&
		expValEquals('web_panel_query_batch_call', 'isEnabled', true)
	) {
		result.then(({ data }) => {
			if (data?.webPanels) {
				// Pre-group panels by location using a Map to avoid O(n×m) nested loop complexity
				type WebPanel = NonNullable<(typeof data.webPanels)[number]>;
				const panelsByLocation = new Map<string, WebPanel[]>();
				data.webPanels.forEach((panel) => {
					if (panel?.location) {
						const panels = panelsByLocation.get(panel.location) ?? [];
						panels.push(panel);
						panelsByLocation.set(panel.location, panels);
					}
				});

				// Write batched cache key with all panels so consumers using the array
				// variables also get a cache hit in Apollo
				writeQueryInterop<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>({
					query: WebPanelLocationQuery,
					variables: {
						location: null,
						locations: location,
						contentId,
						spaceKey,
					},
					data: {
						webPanels: data.webPanels,
					},
				});

				// Write individual cache keys for each location so consumers using
				// single location variables get a cache hit
				location.forEach((loc) => {
					writeQueryInterop<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>({
						query: WebPanelLocationQuery,
						variables: {
							location: loc,
							locations: [],
							contentId,
							spaceKey,
						},
						data: {
							webPanels: panelsByLocation.get(loc) ?? [],
						},
					});
				});
			}
		});
	}

	return result;
}
